import React, { useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery } from 'react-query'
import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet-async'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../components/ui/SafeIcon'
import LoadingSpinner from '../components/ui/LoadingSpinner'
import { getProduct } from '../services/api'
import { useCartStore } from '../store/cartStore'
import { useMessagesStore } from '../store/messagesStore'
import { useAuthStore } from '../store/authStore'

const { FiMessageSquare, FiShield, FiStar, FiHeart, FiShare2 } = FiIcons

function ProductPage() {
  const { id } = useParams()
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const [quantity, setQuantity] = useState(100)
  const addItem = useCartStore(state => state.addItem)
  const navigate = useNavigate()
  const { ensureThread } = useMessagesStore()
  const { isAuthenticated, user } = useAuthStore()

  const { data: product, isLoading, error } = useQuery(
    ['product', id],
    () => getProduct(id),
    { enabled: !!id }
  )

  const handleAddToCart = () => {
    addItem(product, quantity)
  }

  const handleContactSupplier = () => {
    if (!isAuthenticated) {
      navigate('/login')
      return
    }
    
    const thread = ensureThread({
      userEmail: user?.email,
      supplierId: product.supplier.name,
      supplierName: product.supplier.name
    })
    
    navigate('/messages', { state: { threadId: thread.id } })
  }

  if (isLoading) return <div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>
  if (error || !product) return <div className="min-h-screen flex items-center justify-center">{t('common.error')}</div>

  const getLocalized = (obj) => {
    if (!obj) return ''
    if (typeof obj === 'string') return obj
    return obj[lang] || obj['en'] || obj['de'] || ''
  }

  const name = getLocalized(product.name)
  const description = getLocalized(product.description)

  const tiers = [
    { qty: '1-99', price: product.price * 1.2 },
    { qty: '100-999', price: product.price },
    { qty: '≥1000', price: product.price * 0.9 }
  ]

  return (
    <>
      <Helmet>
        <title>{name} | 1688Euro.com</title>
      </Helmet>

      <div className="bg-white border-b border-gray-200">
        <div className="max-w-[1400px] mx-auto px-4 py-3 text-xs text-gray-500">
          <Link to="/" className="hover:text-[#FF6600]">{t('nav.home')}</Link> &gt;
          <Link to="/search" className="hover:text-[#FF6600] mx-1">{t('nav.categories')}</Link> &gt;
          <span className="text-gray-900 mx-1 truncate">{name}</span>
        </div>
      </div>

      <div className="min-h-screen bg-[#F2F3F7] py-6">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="lg:w-[380px] flex-shrink-0">
                <div className="aspect-square bg-gray-50 rounded-lg overflow-hidden border border-gray-200 mb-4 relative group">
                  <img src={product.image} alt={name} className="w-full h-full object-contain" />
                  <div className="absolute top-4 right-4 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button className="p-2 bg-white rounded-full shadow hover:text-[#FF6600]"><SafeIcon icon={FiHeart} className="w-5 h-5" /></button>
                    <button className="p-2 bg-white rounded-full shadow hover:text-[#FF6600]"><SafeIcon icon={FiShare2} className="w-5 h-5" /></button>
                  </div>
                </div>
                <div className="grid grid-cols-5 gap-2">
                  {product.images?.map((img, i) => (
                    <div key={i} className="aspect-square bg-white rounded border border-gray-200 cursor-pointer hover:border-[#FF6600] overflow-hidden">
                      <img src={img} alt={`${name} ${i}`} className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-xl font-bold text-gray-900 mb-4 leading-snug">{name}</h1>
                
                <div className="bg-gray-50 p-4 rounded-md mb-6">
                  <div className="flex justify-between items-center text-sm mb-2 text-gray-500">
                    {tiers.map((tier, i) => (
                      <div key={i} className={`flex-1 ${i !== 0 ? 'border-l border-gray-200 pl-4' : ''}`}>
                        <div className="text-gray-500 mb-1">{tier.qty} {t('product.pieces')}</div>
                        <div className="text-xl font-bold text-gray-900">{tier.price.toFixed(2)} €</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-4 mb-6 text-sm">
                  <div className="flex">
                    <span className="w-24 text-gray-500 flex-shrink-0">{t('product.minOrder')}:</span>
                    <span className="text-gray-900">{product.minOrderQuantity} {t('product.pieces')}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="w-24 text-gray-500 flex-shrink-0">{t('product.protection')}:</span>
                    <div className="flex items-center text-[#FF6600] text-xs font-medium">
                      <SafeIcon icon={FiShield} className="w-4 h-4 mr-1" />
                      {t('feature.tradeAssurance')}
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-100 my-4"></div>

                <div className="flex flex-col gap-3">
                  <div className="flex gap-4 items-center">
                    <span className="text-sm font-bold text-gray-700">{t('product.pieces')}:</span>
                    <input 
                      type="number" 
                      value={quantity} 
                      onChange={(e) => setQuantity(parseInt(e.target.value) || 0)}
                      className="w-24 px-3 py-2 border border-gray-300 rounded focus:border-[#FF6600] focus:outline-none"
                    />
                  </div>
                  <div className="flex gap-4 mt-2">
                    <button className="flex-1 bg-[#FF6600] text-white font-bold py-3 px-6 rounded hover:bg-[#e55c00] transition-colors">
                      {t('product.startOrder')}
                    </button>
                    <button onClick={handleAddToCart} className="flex-1 border border-[#FF6600] text-[#FF6600] font-bold py-3 px-6 rounded hover:bg-orange-50 transition-colors">
                      {t('product.addToCart')}
                    </button>
                  </div>
                </div>
              </div>

              <div className="lg:w-80 flex-shrink-0">
                <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm h-full">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-gray-400 font-bold">
                      {product.supplier.name.substring(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 text-sm line-clamp-2">{product.supplier.name}</h3>
                      <div className="text-xs text-gray-500 mt-1">China</div>
                    </div>
                  </div>
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">{t('product.businessType')}:</span>
                      <span className="text-gray-900">Manufacturer</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">{t('suppliers.years')}:</span>
                      <span className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-bold">{product.supplier.years}</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <button 
                      onClick={handleContactSupplier}
                      className="w-full border border-[#FF6600] text-[#FF6600] font-medium py-2 rounded hover:bg-orange-50 transition-colors flex items-center justify-center gap-2"
                    >
                      <SafeIcon icon={FiMessageSquare} className="w-4 h-4" />
                      {t('product.chatNow')}
                    </button>
                    <button className="w-full border border-gray-300 text-gray-700 font-medium py-2 rounded hover:bg-gray-50 transition-colors">
                      {t('product.contactSupplier')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-6">
            <div className="flex-1 bg-white border border-gray-200 rounded-lg p-6 min-h-[400px]">
              <h2 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">{t('product.description')}</h2>
              <div className="prose prose-sm max-w-none text-gray-600">
                <p className="mb-4">{description}</p>
                <h3 className="font-bold text-gray-900 mt-6 mb-2">{t('product.specifications')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                  {Object.entries(product.specifications || {}).map(([key, value]) => (
                    <div key={key} className="flex border-b border-gray-100 py-2">
                      <span className="text-gray-500 w-32 flex-shrink-0 capitalize">{key}:</span>
                      <span className="text-gray-900">{Array.isArray(value) ? value.join(', ') : value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default ProductPage